import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { MinuteBookEntry, RegisterEntry } from '@syndiqia/governance/contracts';
import { errorMessage, useApi } from '../../api';
import { Alert, Empty, Loading, PageHeader } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useSyndicate } from '../../session';
import { useOpenDocument } from '../DocumentsPage';
import { GovernanceNav } from './GovernanceNav';

/** The register of resolutions (board or assemblies) and the minute book, printable. */
export function RegistersPage() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const g = t.governance;
  const r = g.registers;
  const [scope, setScope] = useState<'board' | 'assembly'>('board');
  const enabled = s.can('governance.read');
  const register = useQuery({ queryKey: [s.tenantId, 'governance', 'register', scope], queryFn: () => api<RegisterEntry[]>(`${s.api}/governance/register?scope=${scope}`), enabled });
  const book = useQuery({ queryKey: [s.tenantId, 'governance', 'minute-book'], queryFn: () => api<MinuteBookEntry[]>(`${s.api}/governance/minute-book`), enabled });
  const open = useOpenDocument();
  if (!enabled) return <Alert tone="info">{g.boardOnly}</Alert>;

  return (
    <>
      <PageHeader
        title={g.title}
        intro={r.intro}
        actions={
          <button type="button" className="button" onClick={() => window.print()}>
            {r.print}
          </button>
        }
      />
      <GovernanceNav />
      <section className="panel register">
        <header className="dash-card-header">
          <h2>{r.resolutions}</h2>
          <div className="segmented" role="group">
            {(['board', 'assembly'] as const).map((x) => (
              <button key={x} type="button" className={scope === x ? 'active' : undefined} onClick={() => setScope(x)}>
                {r.scope[x]}
              </button>
            ))}
          </div>
        </header>
        {register.isPending && <Loading label={t.loading} />}
        {register.isError && <Alert>{errorMessage(register.error, t)}</Alert>}
        {register.data?.length === 0 && <Empty>{r.none}</Empty>}
        {!!register.data?.length && (
          <table className="table table-stack">
            <thead>
              <tr>
                <th>{r.number}</th>
                <th>{r.date}</th>
                <th>{r.resolution}</th>
                <th>{r.decision}</th>
              </tr>
            </thead>
            <tbody>
              {register.data.map((e) => (
                <tr key={e.number} className={e.adopted ? undefined : 'muted'}>
                  <td data-label={r.number}>{e.number}</td>
                  <td data-label={r.date}>{date(e.date)}</td>
                  <td data-label={r.resolution}>
                    <strong>{e.title}</strong>
                    {e.text && <p className="small resolution">{e.text}</p>}
                    <span className="muted small">
                      {e.source === 'written' ? r.written : e.meetingId ? <Link to={`${s.base}/governance/${e.meetingId}`}>{e.meetingTitle}</Link> : null}
                    </span>
                  </td>
                  <td data-label={r.decision} className="small">
                    {!e.adopted && <span className="badge badge-urgent">{r.rejected}</span>} {e.decision}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="panel register">
        <h2>{r.minuteBook}</h2>
        {book.data?.length === 0 && <Empty>{r.minuteBookNone}</Empty>}
        <ul className="compact-list">
          {book.data?.map((e) => (
            <li key={`${e.kind}-${e.id}`}>
              <span>
                <span className={`badge badge-meeting-${e.kind}`}>{e.kind === 'written' ? r.written : g.kinds[e.kind]}</span> {e.title}
              </span>
              <span className="muted small">{date(e.date)}</span>
              <span>
                {e.documentId ? (
                  <button type="button" className="link-button small" onClick={() => void open({ id: e.documentId! })}>
                    {r.open}
                  </button>
                ) : e.kind === 'written' ? (
                  <Link className="small" to={`${s.base}/governance/written`}>
                    {r.open}
                  </Link>
                ) : (
                  <Link className="small" to={`${s.base}/governance/${e.id}`}>
                    {r.open}
                  </Link>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
